import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  fetchPublicElevatorConflict,
  submitElevatorConflict,
  type PublicElevatorConflictInfo,
} from '../api/elevatorAppointments';

function formatDateTime(iso: string | null): string {
  if (!iso) return 'To be confirmed';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'To be confirmed';
  return d.toLocaleString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const wrapStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: '#f3f4f6',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '2.5rem 1rem',
  fontFamily: 'Arial, Helvetica, sans-serif',
  color: '#1b1c1e',
};
const cardStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: 12,
  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  maxWidth: 620,
  width: '100%',
  padding: '2rem',
};

const ElevatorConflict: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [info, setInfo] = useState<PublicElevatorConflictInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError('Invalid link.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchPublicElevatorConflict(token)
      .then((data) => !cancelled && setInfo(data))
      .catch((e) => !cancelled && setLoadError(e instanceof Error ? e.message : 'Failed to load appointment.'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async () => {
    if (!token) return;
    if (!reason.trim()) {
      setSubmitError('Please describe why the appointment does not work.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      await submitElevatorConflict(token, reason.trim(), name.trim() || undefined);
      setSubmitted(true);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Failed to submit.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={wrapStyle}>
      <div style={cardStyle}>
        <h1 style={{ fontSize: '1.4rem', marginTop: 0, color: '#1f4b31' }}>Report a Scheduling Conflict</h1>
        {loading ? (
          <p>Loading…</p>
        ) : loadError ? (
          <p style={{ color: '#b91c1c' }}>{loadError}</p>
        ) : submitted ? (
          <div>
            <p style={{ fontSize: '1.05rem' }}>Thank you. Your message has been sent to our elevator team.</p>
            <p style={{ color: '#6b7280' }}>We will reach out to reschedule your appointment as soon as possible.</p>
          </div>
        ) : info ? (
          <>
            <p style={{ color: '#374151' }}>
              Your elevator appointment for <strong>{info.business_name}</strong> is currently scheduled for:
            </p>
            <p
              style={{
                background: '#f8f9fa',
                border: '1px solid #e2e5e9',
                borderRadius: 8,
                padding: '0.75rem 1rem',
                fontWeight: 600,
              }}
            >
              {formatDateTime(info.appointment_at)}
            </p>
            <p style={{ color: '#374151' }}>
              If this date or time does not work for you, please let us know below and our team will follow up to
              reschedule.
            </p>

            <div style={{ marginTop: '1.25rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem' }}>Your name (optional)</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={submitting}
                style={{
                  width: '100%',
                  padding: '0.6rem 0.75rem',
                  borderRadius: 8,
                  border: '1px solid #cbd2d9',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem' }}>
                Why doesn't this appointment work?
              </label>
              <textarea
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setSubmitError('');
                }}
                disabled={submitting}
                rows={7}
                placeholder="Please describe the conflict and any dates/times that would work better…"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: 8,
                  border: '1px solid #cbd2d9',
                  fontSize: '1rem',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {submitError ? <p style={{ color: '#b91c1c', marginTop: '0.75rem' }}>{submitError}</p> : null}

            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={submitting}
              style={{
                marginTop: '1.25rem',
                background: '#2f6f44',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '0.7rem 1.5rem',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: submitting ? 'default' : 'pointer',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? 'Sending…' : 'Submit'}
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default ElevatorConflict;
